import { Question } from '@jupiterone/jupiter-types/question';
import { CloseButton } from '@jupiterone/jupiter-web-toolkit';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Formik } from 'formik';
import React from 'react';
import { populateQuestionQueriesResultsAreField } from '../../questions';
import { JupiterTheme } from '../../theme/types';
import { useSnackbarNotification } from '../notifications/SnackbarNotificationContext';
import QuestionModalFields from './QuestionModalFields';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    rootBackdrop: {
      backgroundColor: 'rgba(255,255,255, 0.18)',
    },
    dialogPaper: {
      background: theme.palette.background.dark,
      width: '100%',
      maxWidth: theme.spacing(100),
      color: theme.palette.coolGrey[50],
    },
    dialogTitle: {
      '& h2': {
        color: theme.palette.coolGrey[50],
      },
      borderBottom: `solid 1px ${theme.palette.grey[400]}`,
    },
    dialogContent: {
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.background.main,
      padding: theme.spacing(2.5),
    },
    dialogForm: {
      marginBottom: 0,
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dialogActions: {
      background: theme.palette.background.main,
      margin: 0,
      padding: theme.spacing(2, 3),
    },
    dialogDismiss: {
      background: theme.palette.background.default,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(1),
    },
  })
);

interface QuestionModalProps {
  open: boolean;
  onClose: VoidFunction;
  onSubmitQuestion: (question: Question) => Promise<void>;
  question: Question;
  complianceStandards?: string[];
  successText: string;
  busyText: string;
  defaultText: string;
  buttonText: string;
  disabled: boolean;
  isInitialValid: boolean;
}

const QuestionModal = ({
  open,
  onClose,
  onSubmitQuestion,
  question,
  complianceStandards = [],
  successText,
  busyText,
  defaultText,
  buttonText,
  disabled,
  isInitialValid,
}: QuestionModalProps) => {
  const classes = useStyles();
  const notification = useSnackbarNotification();

  const [formValues, setFormValues] = React.useState<Question>(
    populateQuestionQueriesResultsAreField(question)
  );

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.title) {
      errors.title = 'required';
    }

    const queriesErrors: any[] = [];
    values.queries?.forEach((queryData: any, index: number) => {
      const queryErrors: any = {};

      if (!queryData || !queryData.query) {
        queryErrors.query = 'required';
        queriesErrors[index] = queryErrors;
      }
    });

    if (queriesErrors.length) {
      errors.queries = queriesErrors;
    }

    const complianceErrors: any[] = [];

    values.compliance?.forEach((complianceStandard: any, index: number) => {
      const errors: any = {};

      if (!complianceStandard || !complianceStandard.standard) {
        errors.standard = 'required';
        complianceErrors[index] = errors;
      }
    });

    if (complianceErrors.length) {
      errors.compliance = complianceErrors;
    }

    return errors;
  };

  const [busy, setBusy] = React.useState<boolean>(false);

  const title = busy ? busyText : defaultText;

  const handleSubmitForm = async (values: any) => {
    setBusy(true);
    await onSubmitQuestion(values);
    setBusy(false);
    notification.success(successText);
  };

  return (
    <Formik
      onSubmit={handleSubmitForm}
      validate={validate}
      initialValues={formValues}
      validateOnBlur
      validateOnChange
      enableReinitialize
      isInitialValid={isInitialValid}
    >
      {({ handleSubmit, handleChange, values, isValid }) => {
        const disableSubmit = busy || !isValid;

        return (
          <form className={classes.dialogForm}>
            <Dialog
              open={open}
              onClose={onClose}
              scroll="paper"
              BackdropProps={{
                classes: {
                  root: classes.rootBackdrop,
                },
              }}
              classes={{ paper: classes.dialogPaper }}
            >
              <DialogTitle className={classes.dialogTitle}>
                {title}
                <CloseButton
                  className={classes.closeButton}
                  onClose={onClose}
                />
              </DialogTitle>
              <DialogContent classes={{ root: classes.dialogContent }}>
                {busy ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                ) : (
                  <>
                    <QuestionModalFields
                      disabled={disabled}
                      question={values}
                      onChange={handleChange}
                      setFormValues={setFormValues}
                      complianceStandards={complianceStandards}
                    />
                  </>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  disabled={disableSubmit}
                  variant="contained"
                  onClick={handleSubmit as any}
                  color="primary"
                >
                  {buttonText}
                </Button>
                <Button
                  className={classes.dialogDismiss}
                  variant="contained"
                  onClick={onClose}
                >
                  Dismiss
                </Button>
              </DialogActions>
            </Dialog>
          </form>
        );
      }}
    </Formik>
  );
};

export default QuestionModal;
